/* eslint-disable require-jsdoc */
import {ControlledEZPRSelect} from '../../utils/ControlledEZPRSelect.jsx';
import React, {useEffect, useRef, useState} from 'react';
import {TextField, Stack, Checkbox, Typography} from '@mui/material';
import PropTypes from 'prop-types';
import {MasterDataDropdown} from '../../utils/MasterDataDropdown.jsx';
import {DeliveryAddressField} from './DeliveryAddressField.jsx';
import {checkLabelValueObject} from '../../../../utils/checkObjects';
import {fetchAddressFromPlant, fetchAddressFromStorageLocation} from '../../../../utils/address';
import {requestStores, useApplicationStore} from '../../../ListOfPREditors.brick.jsx';
import {PartnerFunctionField} from './PartnerFunctionField.jsx';
import {PaymentTermsField} from './PaymentTermsField.jsx';
import {AdapterDateFns} from '@mui/x-date-pickers/AdapterDateFns';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {DatePicker} from '@mui/x-date-pickers/DatePicker';
import enUS from 'date-fns/locale/en-US';
import {
    getERSAndGRLambda,
    getERSAndGRWithPromiseLambda,
    getMasterDataLambda,
    getNewPaymentTermsLambda,
} from '../../../../utils/functionSignatures';
import CircularProgress from '@mui/material/CircularProgress';
import {PurchaseRequest} from '../../../../models/PurchaseRequest';
import Tooltip from '@mui/material/Tooltip';
import {getCurrencyFromCompanyCodeLambda} from '../../../../utils/functionSignatures';
import {
    invertedSupplierStatusPriorities, LAMBDA_NAMES, mandatoryFieldsForRequester,
    mandatoryFieldsForSpender,
    supplierStatusPriorities,
} from '../../../../utils/constants';
import {MasterDataController} from '../../../../controllers/MasterDataController';
import {bgColorRequester, bgColorSpender} from '../../../_helpers';
import {Incoterms} from './Incoterms.jsx';
import {isDateValid} from '../../../../utils/date.js';
import {fetchAndProcessIncoterms} from '../../../../utils/incotermsHelpers';
import {BrickContext} from 'olympe';
import {useFieldEditability} from '../../utils/useFieldEditability.js';

const GoodReceiptRequiredTooltip = () => <div>
    <p>Good receipts ensure we only pay our suppliers what we confirm we received.</p>
    <p><b>Do not untick this box unless your market policy allows it </b>in order to avoid possible issues later on in the process.</p>
</div>;

export function HeaderDetails(props) {
    const getMasterData = props.context.get(LAMBDA_NAMES.GET_MASTER_DATA);
    const getERSAndGR = props.context.get(LAMBDA_NAMES.GET_ERS_AND_GR);
    const getNewPaymentTerms = props.context.get(LAMBDA_NAMES.GET_NEW_PAYMENT_TERMS);
    const getCurrencyByCompanyCode = props.context.get(LAMBDA_NAMES.GET_CURRENCY_BY_COMPANY_CODE);
    const getSupplierPurchaseOrgLink = props.context.get(LAMBDA_NAMES.GET_SUPPLIER_PURCHASE_ORG_LINK);
    const getAllIncoterms = props.context.get(LAMBDA_NAMES.GET_ALL_INCOTERMS);

    const [loading, setLoading] = useState(false);
    const [deliveryAddressLoading, setDeliveryAddressLoading] = useState(false);

    const useRequestStore = requestStores.get(props.prTag);
    const wayOfBuying = useRequestStore((state) => state.wayOfBuying.currentValue);
    const [companyCode, setCompanyCode, pslCompanyCodes] = useRequestStore((state) => [state.companyCode, state.setCompanyCode, state.pslCompanyCodes]);
    const [purchaseOrganization, setPurchaseOrganization] = useRequestStore((state) => [state.purchaseOrganization, state.setPurchaseOrganization]);
    const [purchasingGroup, setPurchasingGroup] = useRequestStore((state) => [state.purchasingGroup, state.setPurchasingGroup]);
    const [plant, setPlant, plants] = useRequestStore((state) => [state.defaultPlant, state.setDefaultPlant, state.plants]);
    const [storageLocation, setStorageLocation] = useRequestStore((state) => [state.defaultStorageLocation, state.setDefaultStorageLocation]);
    const [deliveryAddress, setDeliveryAddress] = useRequestStore((state) => [state.defaultDeliveryAddress, state.setDefaultDeliveryAddress]);
    const [validityStart, setValidityStart] = useRequestStore((state) => [state.validityStart, state.setValidityStart]);
    const [validityEnd, setValidityEnd] = useRequestStore((state) => [state.validityEnd, state.setValidityEnd]);
    const [requestedDeliveryDate, setRequestedDeliveryDate] = useRequestStore((state) => [state.defaultRequestedDeliveryDate, state.setDefaultRequestedDeliveryDate]);
    const [multiplePlants, multipleStorageLocations, multipleDeliveryAddress] = useRequestStore((state) => [state.multiplePlants, state.multipleStorageLocations, state.multipleDeliveryAddress]);
    const [newPaymentTerms, setNewPaymentTerms] = useRequestStore((state) => [state.newPaymentTerms, state.setNewPaymentTerms]);
    const [supplierStatus, setSupplierStatus] = useRequestStore((state) => [state.supplierStatus, state.setSupplierStatus]);
    const areThereMultipleOrderingAddresses = useRequestStore((state) => state.areThereMultipleOrderingAddresses);
    const areThereMultipleInvoicePartners = useRequestStore((state) => state.areThereMultipleInvoicePartners);
    const vendorCode = useRequestStore((state) => state.vendorCode);
    const status = useRequestStore((state) => state.status);
    const userIdToCallSAPAPIs = useRequestStore((state) => state.userIdToCallSAPAPIs);
    const [goodsReceipt, setGoodsReceipt] = useRequestStore((state) => [state.goodsReceipt, state.setGoodsReceipt]);
    const [ers, setErs] = useRequestStore((state) => [state.ers, state.setErs]);
    const [grBasedInvoiceVerification, setGrBasedInvoiceVerification] = useRequestStore((state) => [state.grBasedInvoiceVerification, state.setGrBasedInvoiceVerification]);
    const setCompanyCodeCurrency = useRequestStore((state) => state.setCompanyCodeCurrency);
    const materials = useRequestStore((state) => state.materials);
    const [highlightMandatoryFieldForRequester, highlightMandatoryFieldForSpender] = useRequestStore((state) => [state.highlightMandatoryFieldForRequester, state.highlightMandatoryFieldForSpender]);

    const {isDisabled, tab} = useFieldEditability(props.prTag, {context: props.context});
    const submittedRequestsTab = tab === PurchaseRequest.tab.submitted;

    const dateFormat = useApplicationStore((state) => state.userDateFormat);
    const minDate = new Date(Date.now());

    const [defaultValues, setDefaultValues] = useState({
        grBasedInvoiceVerification: false,
        ers: false,
    });

    const [paymentTermsOptions, setPaymentTermsOptions] = useState([]);
    const [paymentTermTooltipMsg, setPaymentTermTooltipMsg] = useState('');

    const [loadingCheckDependency, setLoadingCheckDependency] = useState(false);
    const isFirstRender = useRef(true);

    const [showPTAutocomplete, setShowPTAutocomplete] = useState(false);

    const setIncoterms = useRequestStore((state) => state.setIncoterms);
    const incoterms = useRequestStore((state) => state.incoterms);

    const handleCompanyCodeChange = (newValue) => {
        setCompanyCode(newValue);
    };

    const handlePurchaseOrganizationChange = async (newValue) => {
        setPurchaseOrganization(newValue);
        setLoading(true);

        const incotermsPromise = fetchAndProcessIncoterms({
            context: props.context,
            getIncoterms: getSupplierPurchaseOrgLink,
            getAllIncoterms: getAllIncoterms,
            vendorCode: vendorCode,
            purchaseOrgValue: newValue.value,
        });

        const getERSAndGRPromise = getERSAndGRWithPromiseLambda(
            props.context,
            getERSAndGR,
            vendorCode,
            newValue.value,
        );

        const [incotermsResult, ersAndGrResult] = await Promise.allSettled([
            incotermsPromise,
            getERSAndGRPromise,
        ]);

        if (incotermsResult.status === 'fulfilled') {
            const {code, description} = incotermsResult.value;
            setIncoterms({
                data: {
                    label: description,
                    value: code,
                },
                manuallyModified: false,
            });
        } else {
            console.error('Error fetching incoterms:', incotermsResult.reason);
            setIncoterms({
                data: {
                    label: '',
                    value: '',
                },
                manuallyModified: false,
            });
        }

        if (ersAndGrResult.status === 'fulfilled') {
            try {
                const ERSAndGRData = JSON.parse(
                    Array.isArray(ersAndGrResult.value) ? ersAndGrResult.value[0] : ersAndGrResult.value,
                );
                setErs(ERSAndGRData[0]?.xersy === 'X');
                setGrBasedInvoiceVerification(ERSAndGRData[0]?.webre === 'X');
            } catch (error) {
                console.error('Error processing ERS and GR data: ', error);
            }
        } else {
            console.error('Error fetching ERS and GR: ', ersAndGrResult.reason);
        }

        setLoading(false);
    };

    const handleGoodReceiptManagement = ({target: {checked, name}}) => {
        const handlers = {
            goodsReceipt: () => setGoodsReceipt(checked),
            grBasedInvoiceVerification: () => setGrBasedInvoiceVerification(checked),
            ers: () => setErs(checked),
        };

        if (name==='goodsReceipt' && checked === false) {
            Object.values(handlers).forEach( (handler) => handler(false));
        } else {
            handlers[name](checked);
        };
    };

    // This useEffect will retrieve the ERS and GR value from LFM1 table when we are opening the PR and the PR has not been saved
    useEffect(() => {
        // To avoid an error 'Can't perform a React state update on an unmounted component'.
        // Reproduce it -> if we expand the item and then immediately collapse it, getting this error
        let cleanupFunction = false;
        if (status === PurchaseRequest.statuses.created) {
            setLoading(true);
            getERSAndGRLambda(
                props.context,
                getERSAndGR,
                vendorCode,
                purchaseOrganization,
                (result) => {
                    if (!cleanupFunction) {
                        const values = JSON.parse(result);
                        setDefaultValues({
                            ers: values[0]?.xersy === 'X',
                            grBasedInvoiceVerification: values[0]?.webre === 'X',
                        });
                        setErs(values[0]?.xersy === 'X');
                        setGrBasedInvoiceVerification(values[0]?.webre === 'X');
                        setLoading(false);
                    }
                },
                () => setLoading(false),
            );
        } else {
            setDefaultValues({
                ers: false,
                grBasedInvoiceVerification: false,
            });
            setErs(false);
            setGrBasedInvoiceVerification(false);
        }
        return () => cleanupFunction = true;
    }, [vendorCode, status, purchaseOrganization, getERSAndGR, props.context, setErs, setGrBasedInvoiceVerification]);

    useEffect(() => {
        // To avoid an error 'Can't perform a React state update on an unmounted component'.
        // Reproduce it -> if we expand the item and then immediately collapse it, getting this error
        let cleanupFunction = false;
        if (companyCode.value) {
            setLoading(true);
            getNewPaymentTermsLambda(
                props.context,
                getNewPaymentTerms,
                companyCode.value,
                userIdToCallSAPAPIs,
                purchaseOrganization.value,
                (result) => {
                    if (!cleanupFunction) {
                        const parsedData = JSON.parse(result);
                        if (!parsedData || parsedData.length === 0) {
                            getNewPaymentTermsLambda(
                                props.context,
                                getNewPaymentTerms,
                                '',
                                userIdToCallSAPAPIs,
                                purchaseOrganization.value,
                                (fallbackResult) => {
                                    if (!cleanupFunction) {
                                        setShowPTAutocomplete(false);
                                        const paymentTermsFallback = JSON.parse(fallbackResult)?.map((data) => {
                                            return {
                                                label: `${data['Term']} - ${data['NameVAT']}`,
                                                value: data['Term'],
                                            };
                                        });
                                        const PTsWithoutDuplicatesFallback = paymentTermsFallback.filter((value, index, self) => {
                                            return index === self.findIndex((t) => t.label === value.label);
                                        });
                                        if ( newPaymentTerms && newPaymentTerms.value === newPaymentTerms.label && PTsWithoutDuplicatesFallback.length > 0) {
                                            const newValue = PTsWithoutDuplicatesFallback.filter((item)=> item.value.toLowerCase() === newPaymentTerms.value.toLowerCase());
                                            if (newValue.length > 0) {
                                                setPaymentTermTooltipMsg('');
                                                if (newValue.length === 1) {
                                                    setNewPaymentTerms(newValue[0]);
                                                } else {
                                                    setShowPTAutocomplete(true);
                                                }
                                            } else if (newPaymentTerms.value !== '') {
                                                setPaymentTermTooltipMsg('Invalid payment term');
                                            } else {
                                                setPaymentTermTooltipMsg('');
                                            }
                                        }
                                        setPaymentTermsOptions(PTsWithoutDuplicatesFallback || []);
                                        setLoading(false);
                                    }
                                },
                                () => setLoading(false),
                            );
                            return;
                        }
                        setShowPTAutocomplete(false);
                        const paymentTerms = parsedData?.map((data) => {
                            return {
                                label: `${data['Term']} - ${data['NameVAT']}`,
                                value: data['Term'],
                            };
                        });
                        const PTsWithoutDuplicates = paymentTerms.filter((value, index, self) => { // Remove duplicates
                            return index === self.findIndex((t) => t.label === value.label);
                        });
                        if ( newPaymentTerms && newPaymentTerms.value === newPaymentTerms.label && PTsWithoutDuplicates.length > 0) {
                            const newValue = PTsWithoutDuplicates.filter((item)=> item.value.toLowerCase() === newPaymentTerms.value.toLowerCase());
                            if (newValue.length > 0) {
                                setPaymentTermTooltipMsg('');
                                if (newValue.length === 1) {
                                    setNewPaymentTerms(newValue[0]);
                                } else {
                                    setShowPTAutocomplete(true);
                                }
                            } else if (newPaymentTerms.value !== '') {
                                setPaymentTermTooltipMsg('Invalid payment term');
                            } else {
                                setPaymentTermTooltipMsg('');
                            }
                        }
                        setPaymentTermsOptions(PTsWithoutDuplicates || []);
                        setLoading(false);
                    }
                },
                () => setLoading(false),
            );
        }
        return () => cleanupFunction = true;
    }, [companyCode.value, userIdToCallSAPAPIs]);

    // This useEffect will determine the supplier status of the PR. With version 2023.07.18 and before, the supplier status was
    // stored at PR level. After that, each line item has a supplier status, and the PR supplier status is the least important.
    // The priority order is the following: 1. Mandatory 2. SoleSource 3. Validated 4. Blank.
    useEffect(() => {
        setSupplierStatus(supplierStatusFromMaterials(new Set(materials.map((m) => m.supplierStatus))));
    }, [materials]);

    // This useEffect listens for company code changes and checks the dependencies
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        if (!companyCode.value) {
            return;
        }

        setLoadingCheckDependency(true);

        const plantPromise = getMasterDataLambda(props.context, getMasterData, 'Plant', '', companyCode.value, '', '', '', '', '', userIdToCallSAPAPIs);
        const paymentTermsPromise = getMasterDataLambda(props.context, getMasterData, 'Payment Terms', newPaymentTerms.value, companyCode.value, '', '', '', '', '', userIdToCallSAPAPIs);

        Promise.all([plantPromise, paymentTermsPromise])
            .then((values) => {
                const formattedPlants = MasterDataController.formatMasterData(
                    values[0],
                    true,
                    'Plant',
                );

                const formattedPaymentTerms = MasterDataController.formatMasterData(
                    values[1],
                    true,
                    'Payment Terms',
                );

                const plantExists = formattedPlants.some((p) => p.value === plant.value);

                if (!plantExists) {
                    setPlant({label: '', value: ''}, undefined);
                    setStorageLocation({label: '', value: ''}, undefined);
                    setDeliveryAddress({label: '', value: ''});
                }

                const paymentTermExists = formattedPaymentTerms.some(
                    (pt) => pt.value === newPaymentTerms.value,
                );

                if (!paymentTermExists) {
                    setNewPaymentTerms({label: '', value: ''});
                }

                setLoadingCheckDependency(false);
            })
            .catch((error) => {
                console.error('Error fetching master data:', error);
                setLoadingCheckDependency(false);
            });
    }, [companyCode.value]);

    /**
     * @param {Set<string>} values
     * @return {string} The supplier status
     */
    const supplierStatusFromMaterials = (values) => {
        const priorities = [...values]
            .filter(Boolean)
            .map((s) => supplierStatusPriorities.get(s))
            .filter((p) => typeof p === 'number');

        if (priorities.length === 0) {
            return supplierStatus;
        }

        const minPriority = Math.min(...priorities);
        return invertedSupplierStatusPriorities.get(minPriority);
    };

    const handlePurchasingGroupChange = (newValue) => {
        setPurchasingGroup(newValue);
    };

    const handlePlantChange = (newValue) => {
        if (!newValue) {
            setPlant({label: '', value: ''}, undefined);
            setDeliveryAddress({label: '', value: ''});
            return;
        }

        setDeliveryAddressLoading(true);
        const plantAddressPromise = newValue.addressNumber ?
            fetchAddressFromPlant(newValue.addressNumber, props.context) :
            Promise.resolve(undefined);

        plantAddressPromise
            .then((plantAddress) => {
                setPlant(newValue, plantAddress);
            })
            .finally(() => setDeliveryAddressLoading(false));
    };

    const handleStorageLocationChange = async (newValue) => {
        setDeliveryAddressLoading(true);

        // If we clear the field, re-apply the plant delivery address
        if (newValue.value === '' && newValue.label === '') {
            try {
                const data = await MasterDataController.fetchMasterData(getMasterData, props.context, 'Plant', plant.value, companyCode, undefined, undefined, undefined, undefined, undefined, userIdToCallSAPAPIs);
                const receivedPlant = MasterDataController.formatMasterData(data, false, 'Plant')[0];

                const plantAddressPromise = receivedPlant.addressNumber ? fetchAddressFromPlant(receivedPlant.addressNumber, props.context) : Promise.resolve(undefined);
                plantAddressPromise.then((plantAddress) => {
                    setPlant(receivedPlant, plantAddress);
                })
                    .finally(() => setDeliveryAddressLoading(false));
                setStorageLocation(newValue, undefined);
            } catch (e) {
                console.error(e);
            }
        } else {
            const storageLocationAddressPromise = newValue ? fetchAddressFromStorageLocation(newValue, props.context, plant, userIdToCallSAPAPIs) : Promise.resolve(undefined);
            storageLocationAddressPromise.then((storageLocationAddress) => {
                setStorageLocation(newValue, storageLocationAddress);
            })
                .finally(() => setDeliveryAddressLoading(false));
        }
    };

    const handleDeliveryAddressChange = (newAddress) => {
        setDeliveryAddress(newAddress);
    };

    const handleValidityStartChanged = (newValue) => {
        if (isDateValid(newValue)) {
            setValidityStart(newValue);
        }
    };

    const handleValidityEndChanged = (newValue) => {
        if (isDateValid(newValue)) {
            setValidityEnd(newValue);
        }
    };

    const handleRequestedDeliveryDate = (newValue) => {
        if (isDateValid(newValue)) {
            setRequestedDeliveryDate(newValue);
        }
    };

    const handlePaymentTermsChange = (_, newValue) => {
        setPaymentTermTooltipMsg('');
        setShowPTAutocomplete(false);
        setNewPaymentTerms(newValue || {label: '', value: ''});
    };

    const [viewMoreInformation, setViewMoreInformation] = useState(false);

    // This useEffect automatically show the viewMoreInformation if we have multiple OA/PI
    useEffect(() => {
        const isAlternatePaymentTermsNotEmpty =
            newPaymentTerms && newPaymentTerms.value && newPaymentTerms.value.trim() !== '';
        const isIncotermsDescriptionNotEmpty =
            incoterms && incoterms.data && incoterms.data.label && incoterms.data.label.trim() !== '';
        const shouldOpen = areThereMultipleOrderingAddresses || areThereMultipleInvoicePartners || isAlternatePaymentTermsNotEmpty || isIncotermsDescriptionNotEmpty;
        setViewMoreInformation((current) => current || shouldOpen);
    }, [areThereMultipleOrderingAddresses, areThereMultipleInvoicePartners, newPaymentTerms, incoterms]);

    // Whenever company code changes, fetch company code currency as reference for calculating total.
    useEffect(() => {
        // To avoid an error 'Can't perform a React state update on an unmounted component'.
        // Reproduce it -> if we expand the item and then immediately collapse it, getting this error
        let cleanupFunction = false;
        if (companyCode.value) {
            getCurrencyFromCompanyCodeLambda(
                props.context,
                getCurrencyByCompanyCode,
                companyCode.value,
                (result) => {
                    if (!cleanupFunction) {
                        setCompanyCodeCurrency(JSON.parse(result)?.[0]?.waers);
                    }
                },
            );
        }
        return () => cleanupFunction = true;
    }, [companyCode.value]);

    const handleClickOnViewMore = () => {
        setViewMoreInformation(!viewMoreInformation);
    };

    return [
        <div className={'pr-editor-header-details-diamond'} key={'generalInfoDiamond' + props.prTag}/>,

        <div className={'pr-editor-header-details-bg'} key={'generalInfoOrange' + props.prTag}>
            <div className={'pr-editor-header-details-container'}>
                <div className={'pr-editor-header-details-content'}>
                    <div/>
                    <div className={'pr-editor-header-details-view-more'}>
                        <p onClick={handleClickOnViewMore} style={{cursor: 'pointer', marginLeft: 'auto'}}>{viewMoreInformation ? 'View less' : 'View more'}&nbsp;</p>
                        <i onClick={handleClickOnViewMore} className="material-icons" style={{cursor: 'pointer', fontSize: '16px', alignSelf: 'center'}} >{viewMoreInformation ? 'visibility_off' : 'visibility'}</i>
                    </div>
                    <p>Company code: *</p>
                    {
                        pslCompanyCodes.length === 0 ? (
                            <MasterDataDropdown
                                recentlyUsed
                                error={!companyCode.label}
                                property={'Company Code'}
                                selectedValue={companyCode}
                                context={props.context}
                                getMasterData={getMasterData}
                                onChange={handleCompanyCodeChange}
                                filterQueryWithSearch
                                userId={userIdToCallSAPAPIs}
                                disabled={isDisabled('header.companyCode')}
                                highlightMandatoryFieldForRequester={highlightMandatoryFieldForRequester}
                                highlightMandatoryFieldForSpender={highlightMandatoryFieldForSpender}
                                mandatoryForSpender={mandatoryFieldsForSpender.includes('Company code')}
                                mandatoryForRequester={mandatoryFieldsForRequester.includes('Company code')}
                                valueFromSelectionOnly={true}
                            />
                        ) : (
                            <ControlledEZPRSelect
                                value={companyCode}
                                options={[...pslCompanyCodes].sort((a, b) => a.value.localeCompare(b.value))}
                                placeholder={'Please choose the Company code'}
                                handleChange={(_, newVal) => {
                                    const newLabelValue = checkLabelValueObject(newVal);
                                    handleCompanyCodeChange(newLabelValue);
                                }}
                                readOnly={isDisabled('header.companyCode')}
                                highlightMandatoryFieldForRequester={highlightMandatoryFieldForRequester}
                                highlightMandatoryFieldForSpender={highlightMandatoryFieldForSpender}
                                mandatoryForSpender={mandatoryFieldsForSpender.includes('Company code')}
                                mandatoryForRequester={mandatoryFieldsForRequester.includes('Company code')}
                                valueFromSelectionOnly={true}
                            />
                        )
                    }

                    <p>Purchase organization: *</p>
                    <MasterDataDropdown
                        error={!purchaseOrganization.label}
                        property={'Purchase Organization'}
                        selectedValue={purchaseOrganization}
                        context={props.context}
                        getMasterData={getMasterData}
                        onChange={handlePurchaseOrganizationChange}
                        filterQueryWithSearch
                        userId={userIdToCallSAPAPIs}
                        recentlyUsed
                        disabled={isDisabled('header.purchaseOrganization')}
                        highlightMandatoryFieldForRequester={highlightMandatoryFieldForRequester}
                        highlightMandatoryFieldForSpender={highlightMandatoryFieldForSpender}
                        mandatoryForSpender={mandatoryFieldsForSpender.includes('Purchase organization')}
                        mandatoryForRequester={mandatoryFieldsForRequester.includes('Purchase organization')}
                    />
                    <div className={'pr-editor-label-with-question-mark'}>
                        <p>Purchasing group: *</p>
                    </div>
                    <MasterDataDropdown
                        error={!purchasingGroup.label}
                        property={'Purchasing Group'}
                        selectedValue={purchasingGroup}
                        context={props.context}
                        getMasterData={getMasterData}
                        onChange={handlePurchasingGroupChange}
                        filterQueryWithSearch
                        userId={userIdToCallSAPAPIs}
                        recentlyUsed
                        disabled={isDisabled('header.purchasingGroup')}
                        highlightMandatoryFieldForRequester={highlightMandatoryFieldForRequester}
                        highlightMandatoryFieldForSpender={highlightMandatoryFieldForSpender}
                        mandatoryForSpender={mandatoryFieldsForSpender.includes('Purchasing group')}
                        mandatoryForRequester={mandatoryFieldsForRequester.includes('Purchasing group')}
                        renderOption={(propsOption, option) => (
                            <li {...propsOption} data-cy={`purchasingGroup_field_option`}>
                                {option.label}
                            </li>
                        )}
                    />
                    {/* <p>Business unit:</p> */}
                    {/* <EZPRSelect inputRef={props.refs.businessUnit} value={props.purchaseRequest.businessUnit} options={[]}/> */}
                    <p>Plant: *</p>
                    {
                        plants.length === 0 ? (
                            <div>
                                <MasterDataDropdown
                                    recentlyUsed
                                    error={!plant.label}
                                    property={'Plant'}
                                    selectedValue={plant}
                                    context={props.context}
                                    getMasterData={getMasterData}
                                    onChange={handlePlantChange}
                                    filterQueryWithSearch
                                    userId={userIdToCallSAPAPIs}
                                    selectedCompanyCode={companyCode}
                                    disabled={isDisabled('header.plant')}
                                    highlightMandatoryFieldForRequester={highlightMandatoryFieldForRequester}
                                    highlightMandatoryFieldForSpender={highlightMandatoryFieldForSpender}
                                    mandatoryForSpender={mandatoryFieldsForSpender.includes('Default plant')}
                                    mandatoryForRequester={mandatoryFieldsForRequester.includes('Default plant')}
                                    loading={loadingCheckDependency}
                                />
                                {multiplePlants() ? <span className={'pr-editor-header-details-multiple-indicator'}>Multiple</span> : undefined}
                            </div>
                        ) : (
                            <ControlledEZPRSelect
                                value={plant.label}
                                options={[...plants].sort((a, b) => a.value.localeCompare(b.value))}
                                placeholder={'Please choose the Plant'}
                                handleChange={(_, newVal) => {
                                    handlePlantChange(newVal);
                                }}
                                readOnly={isDisabled('header.plant')}
                                highlightMandatoryFieldForRequester={highlightMandatoryFieldForRequester}
                                highlightMandatoryFieldForSpender={highlightMandatoryFieldForSpender}
                                mandatoryForSpender={mandatoryFieldsForSpender.includes('Default plant')}
                                mandatoryForRequester={mandatoryFieldsForRequester.includes('Default plant')}
                            />
                        )
                    }
                    <p>Storage location:</p>
                    <div>
                        <MasterDataDropdown
                            property={'Storage Location'}
                            selectedValue={storageLocation}
                            context={props.context}
                            getMasterData={getMasterData}
                            selectedPlant={Array.isArray(plant) ? plant[0] : plant}
                            onChange={handleStorageLocationChange}
                            filterQueryWithSearch
                            userId={userIdToCallSAPAPIs}
                            recentlyUsed
                            disabled={isDisabled('header.storageLocation')}
                            loading={loadingCheckDependency}
                        />
                        {multipleStorageLocations() ? <span className={'pr-editor-header-details-multiple-indicator'}>Multiple</span> : undefined}
                    </div>
                    <p>Delivery address: *</p>
                    <div>
                        <DeliveryAddressField
                            loading={deliveryAddressLoading}
                            value={deliveryAddress}
                            onSave={handleDeliveryAddressChange}
                            disableEdit={isDisabled('header.deliveryAddress')}
                            highlightMandatoryFieldForRequester={highlightMandatoryFieldForRequester}
                            highlightMandatoryFieldForSpender={highlightMandatoryFieldForSpender}
                            mandatoryForSpender={mandatoryFieldsForSpender.includes('Default delivery address')}
                            mandatoryForRequester={mandatoryFieldsForRequester.includes('Default delivery address')}
                        />
                        {multipleDeliveryAddress() ? <span className={'pr-editor-header-details-multiple-indicator'}>Multiple</span> : undefined}
                    </div>
                    {
                        wayOfBuying.value === 'FO' ?
                            <>
                                {/* Here I only found this workaround to place a fake element in the DOM to prevent React from re-utilizing the TextField of Requested delivery date and its value when switching the way of buying */}
                                <p style={{display: 'none'}}>Fake element to avoid REACT wrong optimization</p>
                                <p>Validity Start: *</p>
                                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enUS}>
                                    <DatePicker
                                        value={validityStart || null}
                                        inputFormat={dateFormat || 'dd.MM.yyyy'}
                                        onChange={handleValidityStartChanged}
                                        renderInput={(params) => <TextField {...params} variant={'standard'}
                                            sx={{
                                                backgroundColor: mandatoryFieldsForSpender.includes('Validity start') && highlightMandatoryFieldForSpender?
                                                    bgColorSpender :
                                                    mandatoryFieldsForRequester.includes('Validity start') && highlightMandatoryFieldForRequester ?
                                                        bgColorRequester :
                                                        null,
                                            }}
                                        />}
                                        disabled={isDisabled('header.validityStart')}
                                    />
                                </LocalizationProvider>
                                <p>Validity End: *</p>
                                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enUS}>
                                    <DatePicker
                                        minDate={submittedRequestsTab ? null : minDate}
                                        value={validityEnd || null}
                                        inputFormat={dateFormat || 'dd.MM.yyyy'}
                                        onChange={handleValidityEndChanged}
                                        renderInput={(params) => <TextField {...params} variant={'standard'}
                                            sx={{
                                                backgroundColor: mandatoryFieldsForSpender.includes('Validity end') && highlightMandatoryFieldForSpender ?
                                                    bgColorSpender :
                                                    mandatoryFieldsForRequester.includes('Validity end') && highlightMandatoryFieldForRequester ?
                                                        bgColorRequester :
                                                        null,
                                            }}
                                        />}
                                        disabled={isDisabled('header.validityEnd')}
                                    />
                                </LocalizationProvider>
                            </> :
                            <>
                                <p>Requested delivery date: *</p>
                                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enUS}>
                                    <DatePicker
                                        minDate={submittedRequestsTab ? null : minDate}
                                        value={requestedDeliveryDate || null}
                                        inputFormat={dateFormat || 'dd.MM.yyyy'}
                                        onChange={handleRequestedDeliveryDate}
                                        renderInput={(params) => <TextField {...params} variant={'standard'}
                                            sx={{
                                                backgroundColor: mandatoryFieldsForSpender.includes('Default requested delivery date') && highlightMandatoryFieldForSpender ?
                                                    bgColorSpender :
                                                    mandatoryFieldsForRequester.includes('Default requested delivery date') && highlightMandatoryFieldForRequester ?
                                                        bgColorRequester :
                                                        null,
                                            }}/>}
                                        disabled={isDisabled('header.requestedDeliveryDate')}
                                    />
                                </LocalizationProvider>
                            </>
                    }
                </div>
                <div className={'pr-editor-header-details-vertical-separator'} key={'generalInfoSeparator' + props.prTag} style={{display: viewMoreInformation ? 'block' : 'none'}}/>
                <div className={'pr-editor-header-details-content'} key={'generalInfoRhs' + props.prTag} style={{display: viewMoreInformation ? 'grid' : 'none'}}>
                    <div/>
                    <div/>
                    <div className={'pr-editor-label-with-question-mark'}>
                        <p>Payment terms:</p>
                    </div>
                    <Tooltip title={paymentTermTooltipMsg} open={Boolean(viewMoreInformation && paymentTermTooltipMsg !== '')} placement="top" arrow>
                        <div className={'pr-editor-header-details-label-with-question-mark'}>
                            <PaymentTermsField
                                prTag={props.prTag}
                                context={props.context}/>
                            <div style={{width: '70%'}}>
                                <ControlledEZPRSelect
                                    value={newPaymentTerms}
                                    options={paymentTermsOptions}
                                    placeholder={'Specify new payment terms?'}
                                    handleChange={handlePaymentTermsChange}
                                    readOnly={isDisabled('header.paymentTerms')}
                                    valueFromSelectionOnly={true}
                                    loading={loadingCheckDependency}
                                    openAutocomplete={showPTAutocomplete}
                                />
                            </div>

                        </div>
                    </Tooltip>
                    <div className={'pr-editor-label-with-question-mark'}>
                        <p>{`Ordering address:`}</p>
                    </div>
                    <PartnerFunctionField
                        prTag={props.prTag}
                        purchaseOrganization={purchaseOrganization}
                        partnerFunction={'OA'}
                        context={props.context}
                        disabled={isDisabled('header.orderingAddress')}
                    />
                    <div className={'pr-editor-label-with-question-mark'}>
                        <p>{`Invoice partner:`}</p>
                    </div>
                    <PartnerFunctionField
                        prTag={props.prTag}
                        purchaseOrganization={purchaseOrganization}
                        partnerFunction={'PI'}
                        context={props.context}
                        disabled={isDisabled('header.invoicePartner')}
                    />
                    <p>Incoterms:</p>
                    <Incoterms
                        context={props.context}
                        prTag={props.prTag}
                        loading={loading || props.parentLoading}
                    />
                    <p>Supplier status:</p>
                    <TextField
                        variant={'standard'}
                        value={supplierStatus}
                        disabled={true}
                    />
                    {wayOfBuying.value !== 'FO' &&
                        <>
                            <div className={'pr-editor-label-with-question-mark'}>
                                <p>Good Receipt management type:</p>
                            </div>
                            <Stack direction={'row'} justifyContent={'space-between'}>
                                <Tooltip title={<GoodReceiptRequiredTooltip/>} placement={'top'}>
                                    <div className='pr-editor-header-details-checkbox'>
                                        {loading && <CircularProgress size={20}/>}
                                        <Checkbox
                                            checked={goodsReceipt}
                                            name="goodsReceipt"
                                            disabled={isDisabled('header.goodsReceipt')}
                                            onChange={handleGoodReceiptManagement}
                                        />
                                        <Typography style={{fontSize: 12}}>Good Receipt Required</Typography>
                                    </div>
                                </Tooltip>
                                <div className='pr-editor-header-details-checkbox'>
                                    {loading && <CircularProgress size={20}/>}
                                    <Checkbox
                                        checked={grBasedInvoiceVerification}
                                        name="grBasedInvoiceVerification"
                                        disabled={!defaultValues.grBasedInvoiceVerification || isDisabled('header.grBasedInvoiceVerification')}
                                        onChange={handleGoodReceiptManagement}
                                    />
                                    <Typography style={{fontSize: 12}}>GR-Based Invoice Verification</Typography>
                                </div>
                                <div className='pr-editor-header-details-checkbox'>
                                    {loading && <CircularProgress size={20}/>}
                                    <Checkbox
                                        checked={ers}
                                        name="ers"
                                        disabled={!defaultValues.ers || isDisabled('header.ers')}
                                        onChange={handleGoodReceiptManagement}
                                    />
                                    <Typography style={{fontSize: 12}}>ERS</Typography>
                                </div>
                            </Stack>
                        </>
                    }
                </div>
            </div>
        </div>,
    ];
}

HeaderDetails.propTypes = {
    refs: PropTypes.object,
    prTag: PropTypes.string,
    context: PropTypes.instanceOf(BrickContext).isRequired,
    parentLoading: PropTypes.bool,
};
